import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Mic, Square, Loader as Loader2, RefreshCw, Play, Pause, Video as VideoIcon, Volume2 } from 'lucide-react';
import { RecordRTCPromisesHandler } from 'recordrtc';
import { DraggableVideoPreview } from './DraggableVideoPreview';
import { LyricsDisplay } from './LyricsDisplay';

function isLightColor(hex: string): boolean {
  const c = hex.replace('#', '');
  if (c.length < 6) return false;
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 160;
}

function accentStyle(color: string) {
  return { backgroundColor: color, color: isLightColor(color) ? '#1e293b' : '#ffffff' };
}

type RecordingMode = 'audio' | 'video';

interface KioskRecorderProps {
  backingTrackUrl?: string | null;
  lyricsContent?: string | null;
  maxSeconds?: number;
  mode?: RecordingMode;
  preferredDeviceId?: string;
  initialAudioStream?: MediaStream;
  onComplete: (mediaBlob: Blob, durationSec: number, actualMode: RecordingMode) => void;
  onError?: (message: string) => void;
  accentColor?: string;
}

type Phase = 'idle' | 'requesting' | 'ready' | 'countdown' | 'recording' | 'finalizing' | 'review';

const withTimeout = <T,>(promise: Promise<T>, ms: number, label: string): Promise<T> =>
  Promise.race([
    promise,
    new Promise<T>((_, reject) => setTimeout(() => reject(new Error(`${label} timed out`)), ms)),
  ]);

export const KioskRecorder: React.FC<KioskRecorderProps> = ({
  backingTrackUrl,
  lyricsContent,
  maxSeconds = 180,
  mode: initialMode = 'audio',
  preferredDeviceId,
  initialAudioStream,
  onComplete,
  onError,
  accentColor = '#f97316',
}) => {
  const [phase, setPhase] = useState<Phase>('idle');
  const [mode, setMode] = useState<RecordingMode>(initialMode);
  const [countdown, setCountdown] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [blobMode, setBlobMode] = useState<RecordingMode>('audio');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const [level, setLevel] = useState(0);
  const [lowVolume, setLowVolume] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [songDuration, setSongDuration] = useState(0);
  const [showCamera, setShowCamera] = useState(false);

  const recorderRef = useRef<RecordRTCPromisesHandler | null>(null);
  const nativeRecorderRef = useRef<MediaRecorder | null>(null);
  const nativeChunksRef = useRef<Blob[]>([]);
  const nativeStoppedRef = useRef<Promise<void> | null>(null);
  const combinedStreamRef = useRef<MediaStream | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const videoStreamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const durationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const backingAudioRef = useRef<HTMLAudioElement | null>(null);
  const backingSourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  const backingCtxRef = useRef<AudioContext | null>(null);
  const previewMediaRef = useRef<HTMLAudioElement | HTMLVideoElement | null>(null);
  const meterCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const syncTimestampRef = useRef<number | null>(null);
  const isRecordingRef = useRef(false);
  const isStoppingRef = useRef(false);
  const elapsedRef = useRef(0);
  const stopRecordingRef = useRef<() => void>(() => {});
  const recordingContainerRef = useRef<HTMLDivElement>(null);
  const lowVolumeCountRef = useRef(0);
  const latestBlobRef = useRef<Blob | null>(null);
  const recorderMimeTypeRef = useRef<string>('');

  useEffect(() => () => cleanupAll(), []);

  // Resume AudioContext on visibility change (Chrome mobile suspends it when screen dims)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && isRecordingRef.current) {
        if (backingCtxRef.current && backingCtxRef.current.state === 'suspended') {
          backingCtxRef.current.resume().catch(() => {});
        }
        if (meterCtxRef.current && meterCtxRef.current.state === 'suspended') {
          meterCtxRef.current.resume().catch(() => {});
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  useEffect(() => {
    isRecordingRef.current = phase === 'recording';
    if (phase === 'recording') {
      setLowVolume(false);
      lowVolumeCountRef.current = 0;
      setTimeout(() => {
        recordingContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    }
  }, [phase]);

  useEffect(() => {
    const handleDeviceChange = () => {
      if (!isRecordingRef.current) return;
      const stream = audioStreamRef.current;
      const track = stream?.getAudioTracks()?.[0];
      if (!track || track.readyState === 'ended' || !track.enabled) {
        abortRecording(
          'Recording stopped: your microphone became unavailable. Please try again.'
        );
      }
    };
    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    return () => navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
  }, []);

  const stopAllTracks = () => {
    if (combinedStreamRef.current) {
      combinedStreamRef.current.getTracks().forEach((t) => t.stop());
      combinedStreamRef.current = null;
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((t) => t.stop());
      audioStreamRef.current = null;
    }
    if (videoStreamRef.current) {
      videoStreamRef.current.getTracks().forEach((t) => t.stop());
      videoStreamRef.current = null;
    }
  };

  const teardownBacking = () => {
    if (backingAudioRef.current) {
      backingAudioRef.current.pause();
      backingAudioRef.current.src = '';
      backingAudioRef.current = null;
    }
    backingSourceRef.current = null;
    if (backingCtxRef.current && backingCtxRef.current.state !== 'closed') {
      backingCtxRef.current.close().catch(() => {});
    }
    backingCtxRef.current = null;
  };

  const cleanupAll = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (durationTimerRef.current) clearTimeout(durationTimerRef.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    stopAllTracks();
    teardownBacking();
    if (meterCtxRef.current && meterCtxRef.current.state !== 'closed') {
      meterCtxRef.current.close().catch(() => {});
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  };

  const abortRecording = async (message: string) => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (durationTimerRef.current) { clearTimeout(durationTimerRef.current); durationTimerRef.current = null; }
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (backingAudioRef.current) backingAudioRef.current.pause();
    try {
      if (recorderRef.current) await recorderRef.current.stopRecording();
    } catch { /* ignore */ }
    recorderRef.current = null;
    isStoppingRef.current = false;
    stopAllTracks();
    setShowCamera(false);
    setLevel(0);
    setPhase('idle');
    onError?.(message);
  };

  const setupMeter = (stream: MediaStream) => {
    try {
      const ctx = new AudioContext();
      meterCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      src.connect(analyser);
      analyserRef.current = analyser;
      const buf = new Uint8Array(analyser.frequencyBinCount);
      let frameCount = 0;
      const loop = () => {
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = (buf[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / buf.length);
        const normalized = Math.min(1, rms * 3);
        setLevel(normalized);
        frameCount++;
        if (frameCount % 30 === 0) {
          if (normalized < 0.04) {
            lowVolumeCountRef.current++;
            if (lowVolumeCountRef.current >= 5) setLowVolume(true);
          } else {
            lowVolumeCountRef.current = 0;
            setLowVolume(false);
          }
        }
        rafRef.current = requestAnimationFrame(loop);
      };
      loop();
    } catch {
      // meter is cosmetic
    }
  };

  const loadBackingTrack = async () => {
    if (!backingTrackUrl) return;
    teardownBacking();
    const audio = new Audio(backingTrackUrl);
    audio.crossOrigin = 'anonymous';
    audio.preload = 'auto';
    let endedTriggered = false;
    const onTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      if (isFinite(audio.duration) && audio.duration > 0 && audio.currentTime >= audio.duration - 0.3) {
        triggerEnd();
      }
    };
    const onEnded = () => triggerEnd();
    const triggerEnd = () => {
      if (endedTriggered) return;
      endedTriggered = true;
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('ended', onEnded);
      try { audio.pause(); } catch { /* ignore */ }
      if (durationTimerRef.current) { clearTimeout(durationTimerRef.current); durationTimerRef.current = null; }
      try { stopRecordingRef.current(); } catch { /* ignore */ }
    };
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('loadedmetadata', () => {
      if (isFinite(audio.duration) && audio.duration > 0) {
        setSongDuration(audio.duration);
      }
    });
    backingAudioRef.current = audio;
    try {
      const ctx = new AudioContext();
      backingCtxRef.current = ctx;
      const source = ctx.createMediaElementSource(audio);
      source.connect(ctx.destination);
      backingSourceRef.current = source;
    } catch {
      // fall back to element playback
    }
    try { await audio.load(); } catch { /* ignore */ }
    if (isFinite(audio.duration) && audio.duration > 0) {
      setSongDuration(audio.duration);
    }
  };

  const acquireAudioStream = async () => {
    if (audioStreamRef.current) return audioStreamRef.current;

    // Reuse the live stream passed from the headset check (avoids Bluetooth re-acquisition race)
    if (initialAudioStream) {
      const track = initialAudioStream.getAudioTracks()[0];
      if (track && track.readyState === 'live') {
        audioStreamRef.current = initialAudioStream;
        setupMeter(initialAudioStream);
        return initialAudioStream;
      }
    }

    let deviceId: string | undefined = preferredDeviceId;

    if (!deviceId) {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter((d) => d.kind === 'audioinput');
        const external = audioInputs.find(
          (d) =>
            d.deviceId !== 'default' &&
            d.deviceId !== 'communications' &&
            !/built-in|internal|iphone microphone|ipad microphone/i.test(d.label) &&
            d.label !== ''
        );
        if (external) deviceId = external.deviceId;
      } catch {
        // fall through to default
      }
    }

    const constraints = {
      audio: {
        ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
      },
    };

    // Retry with backoff for Bluetooth devices that are slow to release
    const delays = [0, 300, 600];
    let lastErr: unknown;
    for (const delay of delays) {
      if (delay > 0) await new Promise((r) => setTimeout(r, delay));
      try {
        const audioStream = await navigator.mediaDevices.getUserMedia(constraints);
        audioStreamRef.current = audioStream;
        setupMeter(audioStream);
        return audioStream;
      } catch (err: any) {
        lastErr = err;
        if (err?.name === 'NotAllowedError') throw err;
      }
    }

    // Final fallback: try without exact device constraint
    if (deviceId) {
      try {
        const audioStream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
        });
        audioStreamRef.current = audioStream;
        setupMeter(audioStream);
        return audioStream;
      } catch {
        // throw the original error below
      }
    }

    throw lastErr;
  };

  const handleStart = async () => {
    setBlob(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    if (mode === 'audio') {
      setPhase('requesting');
      try {
        await acquireAudioStream();
        await loadBackingTrack();
        await startCountdown();
      } catch (err: any) {
        setPhase('idle');
        const msg = err?.name === 'NotReadableError'
          ? 'Could not start microphone. Bluetooth devices may need a moment — please try again.'
          : err instanceof Error ? err.message : 'Could not access microphone';
        onError?.(msg);
      }
    } else {
      setPhase('requesting');
      await loadBackingTrack();
      setShowCamera(true);
    }
  };

  const onCameraStreamReady = async (stream: MediaStream) => {
    videoStreamRef.current = stream;
    try {
      await acquireAudioStream();
      setPhase('ready');
    } catch (err: any) {
      setShowCamera(false);
      if (videoStreamRef.current) {
        videoStreamRef.current.getTracks().forEach((t) => t.stop());
        videoStreamRef.current = null;
      }
      setPhase('idle');
      const msg = err?.name === 'NotReadableError'
        ? 'Could not start microphone. Bluetooth devices may need a moment — please try again.'
        : err instanceof Error ? err.message : 'Could not access microphone';
      onError?.(msg);
    }
  };

  const onCameraError = (msg: string) => {
    setShowCamera(false);
    setPhase('idle');
    onError?.(msg);
  };

  const startCountdown = async () => {
    setPhase('countdown');
    setCountdown(3);
    for (let i = 3; i >= 1; i--) {
      setCountdown(i);
      await new Promise((r) => setTimeout(r, 1000));
    }
    setCountdown(0);
    await beginRecording();
  };

  const beginRecording = async () => {
    try {
      let combined: MediaStream;
      if (mode === 'video') {
        const videoStream = videoStreamRef.current;
        const audioStream = audioStreamRef.current;
        if (!videoStream || !audioStream) throw new Error('Streams not ready');
        const existingIds = new Set(videoStream.getAudioTracks().map((t) => t.id));
        audioStream.getAudioTracks().forEach((t) => {
          if (!existingIds.has(t.id)) videoStream.addTrack(t);
        });
        combined = videoStream;
      } else {
        const audioStream = audioStreamRef.current;
        if (!audioStream) throw new Error('Audio stream not ready');
        combined = audioStream;
      }
      combinedStreamRef.current = combined;

      const audioCandidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
      const videoCandidates = [
        'video/webm;codecs=vp8,opus',
        'video/webm;codecs=vp9,opus',
        'video/webm',
        'video/mp4',
      ];
      const candidates = mode === 'audio' ? audioCandidates : videoCandidates;
      const supportedMimeType =
        candidates.find((m) =>
          typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m)
        ) || candidates[0];
      recorderMimeTypeRef.current = supportedMimeType;

      latestBlobRef.current = null;

      const recorder = new RecordRTCPromisesHandler(combined, {
        type: mode === 'audio' ? 'audio' : 'video',
        mimeType: supportedMimeType,
        disableLogs: true,
        numberOfAudioChannels: 2,
        desiredSampRate: 44100,
        audioBitsPerSecond: 128000,
        timeSlice: 1000,
        ondataavailable: (blob: Blob) => {
          if (blob && blob.size > 0) latestBlobRef.current = blob;
        },
        ...(mode === 'video' && {
          videoBitsPerSecond: 1500000,
        }),
      });
      recorderRef.current = recorder;

      nativeChunksRef.current = [];
      nativeStoppedRef.current = null;
      nativeRecorderRef.current = null;
      try {
        const nativeOptions: MediaRecorderOptions = {};
        if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(supportedMimeType)) {
          nativeOptions.mimeType = supportedMimeType;
        }
        nativeOptions.audioBitsPerSecond = 128000;
        if (mode === 'video') {
          nativeOptions.videoBitsPerSecond = 1500000;
        }
        let native: MediaRecorder;
        try {
          native = new MediaRecorder(combined, nativeOptions);
        } catch {
          native = new MediaRecorder(combined);
          recorderMimeTypeRef.current = native.mimeType || supportedMimeType;
        }
        native.ondataavailable = (e: BlobEvent) => {
          if (e.data && e.data.size > 0) nativeChunksRef.current.push(e.data);
        };
        nativeStoppedRef.current = new Promise<void>((resolve) => {
          native.onstop = () => resolve();
          native.onerror = () => resolve();
        });
        native.start(1000);
        nativeRecorderRef.current = native;
      } catch {
        nativeRecorderRef.current = null;
      }

      await recorder.startRecording();
      syncTimestampRef.current = Date.now();

      setElapsed(0);
      elapsedRef.current = 0;
      setCurrentTime(0);
      setPhase('recording');

      if (backingAudioRef.current) {
        try {
          if (backingCtxRef.current && backingCtxRef.current.state === 'suspended') {
            await backingCtxRef.current.resume();
          }
          backingAudioRef.current.currentTime = 0;
          await backingAudioRef.current.play();
          // Fallback timer: stop recording after song duration + 2s buffer.
          // This handles Chrome mobile where 'ended'/'timeupdate' may not fire.
          const dur = backingAudioRef.current.duration;
          if (isFinite(dur) && dur > 0) {
            if (durationTimerRef.current) clearTimeout(durationTimerRef.current);
            durationTimerRef.current = setTimeout(() => {
              durationTimerRef.current = null;
              if (isRecordingRef.current) stopRecordingRef.current();
            }, (dur + 2) * 1000);
          }
        } catch { /* user-gesture via button click */ }
      }

      timerRef.current = setInterval(() => {
        elapsedRef.current += 1;
        const next = elapsedRef.current;
        setElapsed(next);
        if (next >= maxSeconds) stopRecordingRef.current();
      }, 1000);
    } catch (err) {
      onError?.(err instanceof Error ? err.message : 'Failed to start recording');
      stopAllTracks();
      setShowCamera(false);
      setPhase('idle');
    }
  };

  const stopRecording = async () => {
    if (isStoppingRef.current) return;
    isStoppingRef.current = true;

    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (durationTimerRef.current) { clearTimeout(durationTimerRef.current); durationTimerRef.current = null; }
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (meterCtxRef.current && meterCtxRef.current.state !== 'closed') {
      meterCtxRef.current.close().catch(() => {});
      meterCtxRef.current = null;
    }
    if (backingAudioRef.current) {
      try { backingAudioRef.current.pause(); } catch { /* ignore */ }
    }

    setPhase('finalizing');

    const native = nativeRecorderRef.current;
    nativeRecorderRef.current = null;
    if (native) {
      try {
        if (native.state !== 'inactive') {
          try { native.requestData(); } catch { /* ignore */ }
          native.stop();
        }
      } catch {
        // ignore
      }
      try {
        if (nativeStoppedRef.current) {
          await withTimeout(nativeStoppedRef.current, 4000, 'nativeStop');
        }
      } catch {
        // ignore
      }
    }

    const recorder = recorderRef.current;
    if (!recorder) {
      let nativeBlob: Blob | null = null;
      if (nativeChunksRef.current.length > 0) {
        try {
          nativeBlob = new Blob(nativeChunksRef.current, {
            type: recorderMimeTypeRef.current || nativeChunksRef.current[0].type,
          });
        } catch {
          nativeBlob = null;
        }
      }
      setShowCamera(false);
      stopAllTracks();
      teardownBacking();
      if (nativeBlob && nativeBlob.size > 0) {
        setBlob(nativeBlob);
        setBlobMode(mode);
        setPreviewUrl(URL.createObjectURL(nativeBlob));
        setLevel(0);
        setPhase('review');
      } else {
        onError?.('Recording finished but no audio/video was captured. Please try again.');
        setPhase('idle');
      }
      isStoppingRef.current = false;
      return;
    }
    recorderRef.current = null;

    try {
      await withTimeout(recorder.stopRecording(), 5000, 'stopRecording');
    } catch {
      // Continue to attempt blob retrieval; recordrtc may still have data buffered.
    }

    let finalBlob: Blob | null = null;

    if (nativeChunksRef.current.length > 0) {
      try {
        finalBlob = new Blob(nativeChunksRef.current, {
          type: recorderMimeTypeRef.current || nativeChunksRef.current[0].type,
        });
      } catch {
        finalBlob = null;
      }
    }

    if (!finalBlob || finalBlob.size === 0) {
      try {
        finalBlob = await withTimeout(recorder.getBlob(), 8000, 'getBlob');
      } catch {
        try {
          finalBlob = await recorder.getBlob();
        } catch {
          finalBlob = null;
        }
      }
    }

    if (!finalBlob || finalBlob.size === 0) {
      try {
        const internal = await recorder.getInternalRecorder();
        const internalAny = internal as unknown as {
          getArrayOfBlobs?: () => Blob[];
          blob?: Blob;
        };
        if (typeof internalAny.getArrayOfBlobs === 'function') {
          const chunks = internalAny.getArrayOfBlobs();
          if (chunks && chunks.length > 0) {
            finalBlob = new Blob(chunks, { type: recorderMimeTypeRef.current || chunks[0].type });
          }
        }
        if ((!finalBlob || finalBlob.size === 0) && internalAny.blob && internalAny.blob.size > 0) {
          finalBlob = internalAny.blob;
        }
      } catch {
        // ignore, try latestBlobRef next
      }
    }

    if ((!finalBlob || finalBlob.size === 0) && latestBlobRef.current && latestBlobRef.current.size > 0) {
      finalBlob = latestBlobRef.current;
    }

    setShowCamera(false);
    stopAllTracks();
    teardownBacking();

    if (!finalBlob || finalBlob.size === 0) {
      const diag = `mt=${recorderMimeTypeRef.current || 'n/a'} nc=${nativeChunksRef.current.length} lb=${latestBlobRef.current?.size ?? 0}`;
      onError?.(`Recording finished but no audio/video was captured. Please try again. (${diag})`);
      setPhase('idle');
      isStoppingRef.current = false;
      return;
    }

    setBlob(finalBlob);
    setBlobMode(mode);
    setPreviewUrl(URL.createObjectURL(finalBlob));
    setLevel(0);
    setPhase('review');
    isStoppingRef.current = false;
  };

  stopRecordingRef.current = stopRecording;

  const retry = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setBlob(null);
    stopAllTracks();
    setShowCamera(false);
    setPhase('idle');
  };

  const submit = () => {
    if (blob) onComplete(blob, elapsed, blobMode);
  };

  const togglePreview = () => {
    const a = previewMediaRef.current;
    if (!a) return;
    if (a.paused) { a.play(); setIsPreviewPlaying(true); }
    else { a.pause(); setIsPreviewPlaying(false); }
  };

  const displaySeconds = songDuration > 0 ? currentTime : elapsed;
  const minutes = Math.floor(displaySeconds / 60);
  const seconds = Math.floor(displaySeconds % 60);
  const timeStr = `${minutes}:${seconds.toString().padStart(2, '0')}`;
  const progressDenom = songDuration > 0 ? songDuration : maxSeconds;
  const progressPct = Math.min(100, (displaySeconds / progressDenom) * 100);

  return (
    <div className="w-full">
      {phase === 'idle' && (
        <div className="text-center">
          <div className="inline-flex rounded-xl bg-white/5 border border-white/15 p-1 mb-4">
            <button
              onClick={() => setMode('audio')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${mode === 'audio' ? '' : 'text-white/60 hover:text-white'}`}
              style={mode === 'audio' ? accentStyle(accentColor) : { backgroundColor: 'transparent' }}
            >
              <Mic className="w-4 h-4 inline-block mr-1.5 -mt-0.5" /> Audio
            </button>
            <button
              onClick={() => setMode('video')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${mode === 'video' ? '' : 'text-white/60 hover:text-white'}`}
              style={mode === 'video' ? accentStyle(accentColor) : { backgroundColor: 'transparent' }}
            >
              <VideoIcon className="w-4 h-4 inline-block mr-1.5 -mt-0.5" /> Video
            </button>
          </div>
          <div>
            <button
              onClick={handleStart}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold shadow-lg hover:brightness-110 transition"
              style={accentStyle(accentColor)}
            >
              {mode === 'video' ? <VideoIcon className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              {mode === 'video' ? 'Turn on camera & mic' : 'Start Recording'}
            </button>
          </div>
          <p className="text-xs text-white/60 mt-3">
            You get one take. Recording auto-stops when the song ends (max {Math.floor(maxSeconds / 60)}:{(maxSeconds % 60).toString().padStart(2, '0')}).
          </p>
        </div>
      )}

      {phase === 'requesting' && mode === 'audio' && (
        <div className="flex items-center justify-center gap-2 text-white/80 py-6">
          <Loader2 className="w-5 h-5 animate-spin" /> Requesting microphone access...
        </div>
      )}

      {phase === 'finalizing' && (
        <div className="flex flex-col items-center justify-center gap-3 text-white/85 py-10">
          <Loader2 className="w-6 h-6 animate-spin" />
          <p className="text-sm font-medium">Finalizing your take...</p>
          <p className="text-xs text-white/50">Hang tight, this only takes a moment.</p>
        </div>
      )}

      {showCamera && createPortal(
        <div className="fixed inset-0 z-[100] bg-black">
          <DraggableVideoPreview
            onClose={() => { setShowCamera(false); setPhase('idle'); stopAllTracks(); }}
            isRecording={phase === 'recording'}
            useBackgroundBlur={false}
            mirrored={true}
            fullScreen={true}
            onStreamReady={onCameraStreamReady}
            onCameraError={onCameraError}
            videoConstraints={{
              facingMode: 'user',
              width: { ideal: 640, max: 1280 },
              height: { ideal: 480, max: 720 },
              frameRate: { ideal: 24, max: 30 },
            }}
          />

          {phase === 'requesting' && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/60 z-20 pointer-events-none">
              <div className="flex items-center gap-2 text-white/90 bg-black/60 px-4 py-3 rounded-xl">
                <Loader2 className="w-5 h-5 animate-spin" /> Requesting microphone access...
              </div>
            </div>
          )}

          {phase === 'finalizing' && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/70 z-40 pointer-events-none">
              <div className="flex flex-col items-center gap-3 text-white/95 bg-black/70 px-6 py-5 rounded-2xl">
                <Loader2 className="w-6 h-6 animate-spin" />
                <p className="text-sm font-semibold">Finalizing your take...</p>
                <p className="text-xs text-white/60">Hang tight, this only takes a moment.</p>
              </div>
            </div>
          )}

          {phase === 'ready' && (
            <div
              className="absolute bottom-0 left-0 right-0 z-30 px-6 pb-8 pt-10"
              style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.9), transparent)' }}
            >
              <div className="max-w-xl mx-auto flex flex-col items-center gap-4">
                <p className="text-white/90 text-sm text-center">
                  Check your framing. When you're ready, hit start.
                </p>
                <button
                  onClick={startCountdown}
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold shadow-2xl hover:brightness-110 transition"
                  style={accentStyle(accentColor)}
                >
                  <VideoIcon className="w-5 h-5" /> Start Recording
                </button>
              </div>
            </div>
          )}

          {phase === 'countdown' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 z-30">
              <div className="text-9xl font-black animate-pulse drop-shadow-2xl" style={{ color: accentColor }}>
                {countdown}
              </div>
              <p className="text-white/90 mt-4 text-lg font-semibold">Get ready!</p>
            </div>
          )}

          {phase === 'recording' && (
            <>
              <div className="absolute top-4 right-4 z-30 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-sm border border-white/20">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
                <span className="text-red-300 font-semibold text-xs uppercase tracking-wider">Rec</span>
              </div>

              {lyricsContent ? (
                <div className="absolute left-0 right-0 top-16 bottom-36 z-20 overflow-hidden pointer-events-none px-4">
                  <div className="w-full h-full max-w-3xl mx-auto">
                    <LyricsDisplay lrcContent={lyricsContent} currentTime={currentTime} />
                  </div>
                </div>
              ) : (
                <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 z-20 text-center text-white/60 text-sm italic pointer-events-none">
                  No lyrics available
                </div>
              )}

              <div
                className="absolute bottom-0 left-0 right-0 z-30 px-6 pb-6 pt-10"
                style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.9), rgba(0,0,0,0.4) 60%, transparent)' }}
              >
                <div className="max-w-xl mx-auto flex flex-col items-center gap-3">
                  <div className="text-2xl font-bold text-white tabular-nums drop-shadow-lg">{timeStr}</div>
                  <div className="w-full h-1.5 bg-white/20 rounded-full overflow-hidden">
                    <div
                      className="h-full transition-all duration-150 ease-linear"
                      style={{ width: `${progressPct}%`, backgroundColor: accentColor }}
                    />
                  </div>
                  <button
                    onClick={stopRecording}
                    className="mt-1 inline-flex items-center gap-2 px-6 py-3 bg-red-500 hover:bg-red-600 rounded-xl font-semibold text-white shadow-lg"
                  >
                    <Square className="w-5 h-5" /> Stop Recording
                  </button>
                </div>
              </div>
            </>
          )}
        </div>,
        document.body
      )}

      {phase === 'countdown' && mode === 'audio' && (
        <div className="text-center py-8">
          <div className="text-7xl font-black animate-pulse" style={{ color: accentColor }}>
            {countdown}
          </div>
          <p className="text-white/80 mt-3">Get ready!</p>
        </div>
      )}

      {phase === 'recording' && mode === 'audio' && (
        <div ref={recordingContainerRef} className="flex flex-col items-center gap-4">
          <div className="flex items-center justify-center gap-2">
            <span className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
            <span className="text-red-300 font-semibold text-sm uppercase tracking-wider">Recording</span>
          </div>

          {lyricsContent ? (
            <div className="w-full h-80 relative rounded-2xl overflow-hidden bg-black/40 border border-white/10">
              <LyricsDisplay lrcContent={lyricsContent} currentTime={currentTime} />
              <div
                className="absolute top-0 left-0 right-0 h-20 pointer-events-none"
                style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.85), transparent)' }}
              />
              <div
                className="absolute bottom-0 left-0 right-0 h-20 pointer-events-none"
                style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.85), transparent)' }}
              />
            </div>
          ) : (
            <div className="text-sm text-white/60 italic">No lyrics available for this song</div>
          )}

          <div className="text-4xl font-bold text-white tabular-nums">{timeStr}</div>
          <div className="w-full max-w-xs flex items-center gap-2">
            <Volume2 className="w-4 h-4 text-white/40 shrink-0" />
            <div className="flex-1 h-3 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full transition-all duration-75 ease-out"
                style={{ width: `${Math.min(100, level * 100)}%`, backgroundColor: accentColor }}
              />
            </div>
          </div>
          {lowVolume && (
            <p className="text-amber-400 text-xs font-medium animate-pulse">Low mic volume detected</p>
          )}
          <div className="w-full max-w-xs h-1 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full transition-all duration-150 ease-linear"
              style={{ width: `${progressPct}%`, backgroundColor: accentColor }}
            />
          </div>
          <button
            onClick={stopRecording}
            className="inline-flex items-center gap-2 px-6 py-3 bg-red-500 hover:bg-red-600 rounded-xl font-semibold text-white"
          >
            <Square className="w-5 h-5" /> Stop Recording
          </button>
        </div>
      )}

      {phase === 'review' && previewUrl && (
        <div className="text-center py-2">
          {blobMode === 'video' ? (
            <>
              <p className="text-white/80 mb-4">
                This is your one take. Review below, then submit when you're ready.
              </p>
              <video
                ref={(el) => { previewMediaRef.current = el; }}
                src={previewUrl}
                controls
                onEnded={() => setIsPreviewPlaying(false)}
                className="w-full max-w-md mx-auto rounded-xl bg-black aspect-video mb-4"
              />
            </>
          ) : (
            <>
              <p className="text-white/80 mb-4">
                Listen back to your recording, then submit or re-record.
              </p>
              <audio
                ref={(el) => { previewMediaRef.current = el; }}
                src={previewUrl}
                controls
                onEnded={() => setIsPreviewPlaying(false)}
                className="w-full max-w-sm mx-auto mb-4"
              />
            </>
          )}
          <div className="flex gap-3 justify-center">
            <button
              onClick={retry}
              className="inline-flex items-center gap-2 px-5 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl border border-white/15"
            >
              <RefreshCw className="w-4 h-4" /> Re-record
            </button>
            <button
              onClick={submit}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl font-semibold shadow-lg hover:brightness-110"
              style={accentStyle(accentColor)}
            >
              Submit Take
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default KioskRecorder;
